import { useEffect, type RefObject } from "react";

/**
 * 배경 스크롤을 완전히 잠금. body의 overflow:hidden만으로는 이 사이트에서
 * html(documentElement)이 자체 overflow-y:auto를 갖고 있어서 안 막힘 —
 * html도 같이 잠그고, body를 position:fixed로 고정해 iOS 등에서도 확실히 막음.
 *
 * allowScrollRef를 넘기면 그 안(예: 바텀시트 내부 스크롤 영역)의 touchmove는
 * 막지 않음 — 안 그러면 문서 전체에서 touchmove를 막아버려서 오버레이 자체 내부
 * 콘텐츠(필터 바텀시트 등)까지 터치 스크롤이 안 되는 문제가 있었음.
 */
export function useScrollLock(locked: boolean, allowScrollRef?: RefObject<HTMLElement | null>) {
  useEffect(() => {
    if (!locked) return;

    const scrollY = window.scrollY;
    const { style: bodyStyle } = document.body;
    const { style: htmlStyle } = document.documentElement;

    const prevBodyOverflow = bodyStyle.overflow;
    const prevBodyPosition = bodyStyle.position;
    const prevBodyTop = bodyStyle.top;
    const prevBodyWidth = bodyStyle.width;
    const prevHtmlOverflow = htmlStyle.overflow;

    bodyStyle.overflow = "hidden";
    htmlStyle.overflow = "hidden";
    bodyStyle.position = "fixed";
    bodyStyle.top = `-${scrollY}px`;
    bodyStyle.width = "100%";

    const preventTouchMove = (event: TouchEvent) => {
      const allowEl = allowScrollRef?.current;
      if (allowEl && allowEl.contains(event.target as Node)) return;
      event.preventDefault();
    };
    document.addEventListener("touchmove", preventTouchMove, { passive: false });

    return () => {
      bodyStyle.overflow = prevBodyOverflow;
      bodyStyle.position = prevBodyPosition;
      bodyStyle.top = prevBodyTop;
      bodyStyle.width = prevBodyWidth;
      htmlStyle.overflow = prevHtmlOverflow;
      document.removeEventListener("touchmove", preventTouchMove);
      window.scrollTo(0, scrollY);
    };
  }, [locked, allowScrollRef]);
}
